import React, {
  useCallback,
  useMemo,
} from 'react';
import styled from 'styled-components';
import { DEFAULT_THEME_COLOR } from 'appenv';
import BuildingIcon from 'components/icons/Building';
import ChatroomIcon from 'components/icons/Chatroom';
import Routes from 'Routes';
import { SvgIcon } from '@material-ui/core';
import { LocalizedProduct } from 'models/types/products';
import { useSelector } from 'react-redux';
import CompanyLink, { CompanyRouterLink } from 'components/product/CompanyLink';
import CompanyName from 'components/product/CompanyName';
import InsertInvitationIcon from 'components/icons/Calendar';
import { useTranslation } from 'react-i18next';
import isChatroomDisabled from 'components/chat/utils/isChatroomDisabled';
import CircularLoadingIndicator from 'components/indicators/CircularLoadingIndicator';
import useProductBoothData from 'hooks/useProductBoothData';
import useEncodedMeetupUrl from 'hooks/useEncodedMeetupUrl';
import { selectBoothById } from 'models/cms/booths';
import findAvailableLocale from 'models/helpers/findAvailableLocale';
import ProductImageSlider from './ProductImageSlider';

const RootContainer = styled.div`
  display: flex;
  max-width: 1200px;
  flex-shrink: 0;
  width: 90%;
  margin: 0 auto;
  padding-top: 16px;

  @media (max-width: 959px) {
    flex-direction: column;
    align-items: center;
  }
`;

const ProductSummary = styled.div`
  display: inline-block;
  flex-grow: 1;
  padding-left: 16px;

  @media (max-width: 959px) {
    padding-top: 16px;
    padding-left: 0;
    text-align: center;
    width: 100%;
  }
`;

const ProductName = styled.span`
  font-size: 24px;
  font-weight: bold;
`;

const ProductImageContainer = styled.div<{ hasDots: boolean }>`
  width: 100%;
  display: inline-block;
  margin-bottom: ${({ hasDots }) => (hasDots ? '70px' : 0)};
  max-width: 400px;
`;

const ChatLink = styled(CompanyLink)<{ color: string}>`
  color: ${({ color }) => color};

  & svg {
    height: 24px;
  }
`;

const ProductInfo = styled.div`
  padding-top: 24px;
`;

const withFlexBasis = (Component: typeof SvgIcon) => {
  const FlexedComponent = styled(Component)`
    flex-basis: 40px;
  `;
  return <FlexedComponent />;
};

const StyledCompanyName = styled(CompanyName)`
  line-height: 40px;
`;

const ProductHighlightInfo = ({ boothId, productName }: {
  boothId: string;
  productName: string;
}) => {
  const {
    openChatWindow, localizedBoothName, meetupEnabled, appointmentBookingUrl, loading, error,
  } = useProductBoothData(boothId, productName);
  const encodedMeetupUrl = useEncodedMeetupUrl(appointmentBookingUrl);
  const { t } = useTranslation();
  const {
    products: { disableDetailChat, disableDetailMeetup, disableDetailBoothLink },
    themeColor,
    chatroomConfig,
  } = useSelector((s: any) => s.eventConfigs);
  const resolvedColor = themeColor || DEFAULT_THEME_COLOR;

  const booth = useSelector((state) => selectBoothById(state, boothId));
  const boothNameForMatomo = findAvailableLocale(booth?.name || {}, 'en');

  const hideChatLink = useMemo(() => isChatroomDisabled(chatroomConfig), [chatroomConfig]);
  const meetupClick = useCallback(() => {
    window.trackingItemToFirebase(null, 'Booth', 'Meetup', 'Meetup_Click', 'Booth_Profile', boothId, boothNameForMatomo);
  }, [boothId, boothNameForMatomo]);
  if (error) {
    return null;
  }

  if (loading) {
    return <CircularLoadingIndicator />;
  }

  return (
    <ProductInfo>
      {!disableDetailBoothLink && (
        <CompanyRouterLink to={Routes.booth(boothId)}>
          {withFlexBasis(BuildingIcon)}
          <StyledCompanyName>{localizedBoothName}</StyledCompanyName>
        </CompanyRouterLink>
      )}
      {!disableDetailChat && !hideChatLink && boothId && (
        <ChatLink href="#" onClick={openChatWindow} color={resolvedColor}>
          {withFlexBasis(ChatroomIcon)}
          <CompanyName>{t('product.chat_now', 'Chat Now')}</CompanyName>
        </ChatLink>
      )}
      {!disableDetailMeetup && !!meetupEnabled && !!appointmentBookingUrl && (
        <ChatLink href={encodedMeetupUrl} color={resolvedColor} target="_blank" rel="noopener noreferrer" onClick={meetupClick}>
          {withFlexBasis(InsertInvitationIcon)}
          <CompanyName>{t('booth.meetup', 'Meetup')}</CompanyName>
        </ChatLink>
      )}
    </ProductInfo>
  );
};

const ProductHighlightInfoMemo = React.memo(ProductHighlightInfo);

const ProductHighlight = ({
  product: {
    name,
    images,
    boothId,
  },
}: {
  product: LocalizedProduct;
}) => (
  <RootContainer>
    <ProductImageContainer hasDots={images?.length > 1}>
      <ProductImageSlider
        images={images}
        rsProps={{
          dots: images?.length > 1,
        }}
      />
    </ProductImageContainer>
    <ProductSummary>
      <ProductName>
        {name}
      </ProductName>
      <ProductHighlightInfoMemo boothId={boothId} productName={name} />
    </ProductSummary>
  </RootContainer>
);

export default React.memo(ProductHighlight);
